import { useEffect, useState } from 'react';
import { getResourceSignedUrl } from '@/api/resources';

// Module-level cache: resourceVersionId -> { url, exp(ms) }. The admin signed
// URL is valid ~5 min; we expire it slightly early to avoid handing out a
// just-expired URL. State-agnostic on the server (serves draft/pending too).
const cache = new Map<string, { url: string; exp: number }>();
const CACHE_SKEW_MS = 30_000;

/**
 * Drop a cached signed URL so the next consumer re-signs. Call this when the
 * bytes fail to load (e.g. a TTL-expired URL handed out near its boundary) —
 * a fresh mount then fetches a new signature instead of reusing the dead one.
 */
export function invalidateSignedUrl(resourceVersionId: string): void {
  cache.delete(resourceVersionId);
}

/**
 * Resolve a short-lived signed bytes URL for a (bytes) resource version,
 * TTL-cached so the same asset isn't re-signed across surfaces/renders.
 * Pass `null` to disable (e.g. dynamic kinds that have no signed bytes URL).
 */
export function useSignedMediaUrl(resourceVersionId: string | null): {
  url: string | null;
  error: boolean;
} {
  const [url, setUrl] = useState<string | null>(() => {
    if (!resourceVersionId) return null;
    const hit = cache.get(resourceVersionId);
    return hit && hit.exp > Date.now() ? hit.url : null;
  });
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!resourceVersionId) {
      setUrl(null);
      setError(false);
      return;
    }
    const hit = cache.get(resourceVersionId);
    if (hit && hit.exp > Date.now()) {
      setUrl(hit.url);
      setError(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setError(false);
    getResourceSignedUrl(resourceVersionId, { signal: controller.signal })
      .then((res) => {
        if (cancelled) return;
        cache.set(resourceVersionId, {
          url: res.url,
          exp: Date.parse(res.expires_at) - CACHE_SKEW_MS,
        });
        setUrl(res.url);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [resourceVersionId]);

  return { url, error };
}
